import { TrackedGame } from '../types/TrackedGame'
import { TrackingEventExample } from '../types/TrackingEvents'

export enum LiveEventGroupBy {
  Type = 'type',
  Blueprint = 'blueprint',
}

export type LiveEventExamplesByGame = { [gameId: string]: TrackingEventExample[] }

export type LiveEventBlueprintGroup = {
  groupId: string
  typeId: string
  blueprintId: string | undefined
  events: TrackingEventExample[]
}

export type LiveEventExampleGroups = {
  // Type mode: events per event type, only for types the list can render.
  byType: { [typeId: string]: TrackingEventExample[] }
  // Blueprint mode: one group per event type + blueprint (or "no blueprint").
  byBlueprint: LiveEventBlueprintGroup[]
  // What the active mode shows: deduplicated events and their groups.
  eventCount: number
  groupCount: number
}

type GroupLiveEventExamplesParams = {
  filteredEvents?: LiveEventExamplesByGame
  groupBy: LiveEventGroupBy
  hideDiscontinuedBlueprints: boolean
  discontinuedBlueprintIds: Set<string>
  // Type mode leaves out event types the list has no tag for, since it cannot
  // render a card for them. Omit while the tag list is still loading.
  isKnownEventType?: (typeId: string) => boolean
}

export const getDiscontinuedBlueprintIds = (selectedGames: TrackedGame[]): Set<string> => {
  const ids = new Set<string>()
  selectedGames.forEach((trackedGame) => {
    trackedGame.blueprints?.forEach((blueprint) => {
      if (blueprint.discontinued) {
        ids.add(blueprint.blueprintId)
      }
    })
  })
  return ids
}

/** Deduplicates events across games, keeping the earliest start and the latest end. */
export const deduplicateLiveEventExamples = (filteredEvents: LiveEventExamplesByGame = {}): TrackingEventExample[] => {
  const deduped = new Map<string, TrackingEventExample>()

  Object.values(filteredEvents).forEach((gameEvents) => {
    gameEvents.forEach((event) => {
      const existing = deduped.get(event.eventId)
      deduped.set(event.eventId, existing ? { ...existing, start: Math.min(existing.start, event.start), end: Math.max(existing.end, event.end) } : event)
    })
  })

  return Array.from(deduped.values())
}

/**
 * The one grouping behind both the LE Examples list and its "Found N examples
 * in M groups" summary, so the two can never disagree.
 */
export const groupLiveEventExamples = ({
  filteredEvents,
  groupBy,
  hideDiscontinuedBlueprints,
  discontinuedBlueprintIds,
  isKnownEventType,
}: GroupLiveEventExamplesParams): LiveEventExampleGroups => {
  const events = deduplicateLiveEventExamples(filteredEvents)

  if (groupBy === LiveEventGroupBy.Blueprint) {
    const grouped = new Map<string, LiveEventBlueprintGroup>()
    events.forEach((event) => {
      const blueprintId = event.blueprintEvolutionData?.blueprintId
      if (hideDiscontinuedBlueprints && blueprintId && discontinuedBlueprintIds.has(blueprintId)) return

      const groupId = blueprintId ? `${event.typeId}_${blueprintId}` : `${event.typeId}_none`
      const group = grouped.get(groupId) ?? { groupId, typeId: event.typeId, blueprintId, events: [] }
      group.events.push(event)
      grouped.set(groupId, group)
    })

    const byBlueprint = Array.from(grouped.values())
    return {
      byType: {},
      byBlueprint,
      eventCount: byBlueprint.reduce((sum, group) => sum + group.events.length, 0),
      groupCount: byBlueprint.length,
    }
  }

  const byType: { [typeId: string]: TrackingEventExample[] } = {}
  events.forEach((event) => {
    if (isKnownEventType && !isKnownEventType(event.typeId)) return
    if (!byType[event.typeId]) byType[event.typeId] = []
    byType[event.typeId].push(event)
  })

  const typeGroups = Object.values(byType)
  return {
    byType,
    byBlueprint: [],
    eventCount: typeGroups.reduce((sum, typeEvents) => sum + typeEvents.length, 0),
    groupCount: typeGroups.length,
  }
}
